import { AlertCircle, AlertTriangle, CheckCircle, Info, LucideIcon, X } from "lucide-react";

export type MessageAlertVariant = "error" | "warning" | "success" | "info";

interface MessageAlertProps {
  variant: MessageAlertVariant;
  title: string;
  messages: string[];
  onDismiss?: () => void;
  className?: string;
}

interface AlertStyleConfig {
  containerClass: string;
  iconClass: string;
  titleClass: string;
  messageClass: string;
  dismissButtonClass: string;
  bulletClass: string;
  icon: LucideIcon;
}

// Alert style configuration following Open/Closed Principle
const ALERT_STYLES: Record<MessageAlertVariant, AlertStyleConfig> = {
  error: {
    containerClass: "tw-bg-red-50 tw-border-red-200",
    iconClass: "tw-text-red-500",
    titleClass: "tw-text-red-800",
    messageClass: "tw-text-red-700",
    dismissButtonClass: "tw-text-red-400 hover:tw-text-red-600",
    bulletClass: "tw-bg-red-400",
    icon: AlertCircle
  },
  warning: {
    containerClass: "tw-bg-yellow-50 tw-border-yellow-200",
    iconClass: "tw-text-yellow-500",
    titleClass: "tw-text-yellow-800",
    messageClass: "tw-text-yellow-700",
    dismissButtonClass: "tw-text-yellow-400 hover:tw-text-yellow-600",
    bulletClass: "tw-bg-yellow-400",
    icon: AlertTriangle
  },
  success: {
    containerClass: "tw-bg-green-50 tw-border-green-200",
    iconClass: "tw-text-green-500",
    titleClass: "tw-text-green-800",
    messageClass: "tw-text-green-700",
    dismissButtonClass: "tw-text-green-400 hover:tw-text-green-600",
    bulletClass: "tw-bg-green-400",
    icon: CheckCircle
  },
  info: {
    containerClass: "tw-bg-blue-50 tw-border-blue-200",
    iconClass: "tw-text-blue-500",
    titleClass: "tw-text-blue-800",
    messageClass: "tw-text-blue-700",
    dismissButtonClass: "tw-text-blue-400 hover:tw-text-blue-600",
    bulletClass: "tw-bg-blue-400",
    icon: Info
  }
};

/**
 * MessageAlert Component
 *
 * A reusable alert component for displaying multiple messages with different variants.
 * Built with Tailwind CSS following SOLID principles.
 *
 * @example
 * ```tsx
 * <MessageAlert
 *   variant="error"
 *   title="Errors"
 *   messages={["Error 1", "Error 2"]}
 *   onDismiss={() => setErrors([])}
 * />
 * ```
 */
export const MessageAlert = ({
  variant,
  title,
  messages,
  onDismiss,
  className = ""
}: MessageAlertProps) => {
  const styles = ALERT_STYLES[variant];
  const Icon = styles.icon;

  // Don't render if no messages
  if (messages.length === 0) return null;

  return (
    <div className={`tw-border tw-rounded-lg tw-p-3.5 tw-relative ${styles.containerClass} ${className}`}>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className={`tw-absolute tw-top-3 tw-right-3 tw-transition-colors ${styles.dismissButtonClass}`}
          aria-label="Dismiss"
          type="button"
        >
          <X className="tw-h-4 tw-w-4" />
        </button>
      )}
      <div className="tw-flex tw-items-start tw-gap-3">
        <Icon className={`tw-h-5 tw-w-5 tw-flex-shrink-0 tw-mt-0.5 ${styles.iconClass}`} aria-hidden="true" />
        <div className="tw-flex-1">
          <h3 className={`tw-text-sm tw-font-semibold tw-mb-2.5 ${styles.titleClass}`}>{title}</h3>
          <ul className="tw-space-y-1" role="list">
            {messages.map((message, index) => (
              <li key={index} className={`tw-text-sm tw-flex tw-items-start ${styles.messageClass}`}>
                <span
                  className={`tw-inline-block tw-w-1.5 tw-h-1.5 tw-rounded-full tw-mt-1.5 tw-mr-2 tw-flex-shrink-0 ${styles.bulletClass}`}
                  aria-hidden="true"
                />
                <span>{message}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
